import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { Sheet, Maximize2, X, Plus, Trash2 } from "lucide-react";
import Spreadsheet, { CellBase, Matrix } from "react-spreadsheet";
import {
  parseMarkdownToSheets,
  buildXlsxBlobMultiSheet,
  SheetData,
} from "../../utils/buildXlsx";

interface TabBarProps {
  sheets: SheetData[];
  activeTab: number;
  compact?: boolean;
  onTabClick: (idx: number) => void;
  onRenameSheet: (idx: number, newName: string) => void;
  onRemoveSheet: (idx: number) => void;
  onAddSheet: () => void;
}

const TabBar: React.FC<TabBarProps> = ({
  sheets,
  activeTab,
  compact = false,
  onTabClick,
  onRenameSheet,
  onRemoveSheet,
  onAddSheet,
}) => (
  <div className="flex items-center gap-0.5 overflow-x-auto bg-gray-50 border-b border-gray-200 px-2">
    {sheets.map((sheet, idx) => (
      <div
        key={idx}
        className={`group flex items-center gap-1 px-3 py-1.5 text-xs font-medium cursor-pointer border-b-2 transition-colors shrink-0 ${
          idx === activeTab
            ? "border-emerald-600 text-emerald-700 bg-white"
            : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100"
        }`}
        onClick={() => onTabClick(idx)}
        onDoubleClick={() => {
          const newName = prompt("Rename sheet:", sheet.name);
          if (newName) onRenameSheet(idx, newName);
        }}
        title="Double-click to rename"
      >
        <span className="truncate max-w-[120px]">{sheet.name}</span>
        {sheets.length > 1 && !compact && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveSheet(idx);
            }}
            className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-gray-200 rounded transition-opacity"
          >
            <X size={10} />
          </button>
        )}
      </div>
    ))}
    <button
      onClick={onAddSheet}
      className="flex items-center gap-0.5 px-2 py-1.5 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors shrink-0"
      title="Add sheet"
    >
      <Plus size={12} />
    </button>
  </div>
);

interface SpreadsheetExportProps {
  text: string;
  fileName?: string;
}

const SpreadsheetExport: React.FC<SpreadsheetExportProps> = ({
  text,
  fileName = "spreadsheet",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Parse markdown tables into named sheets
  const initialSheets = useMemo(() => parseMarkdownToSheets(text), [text]);

  const [sheets, setSheets] = useState<SheetData[]>(initialSheets);
  const prevSheetCountRef = useRef(initialSheets.length);

  useEffect(() => {
    setSheets(initialSheets);
    // Only reset activeTab when the number of sheets changes (e.g. new table parsed during streaming)
    if (initialSheets.length !== prevSheetCountRef.current) {
      setActiveTab(0);
      prevSheetCountRef.current = initialSheets.length;
    }
  }, [initialSheets]);

  const activeSheet = sheets[activeTab] || sheets[0];

  const handleChange = useCallback(
    (newData: Matrix<CellBase>) => {
      setSheets((prev) =>
        prev.map((sheet, idx) => {
          if (idx !== activeTab) return sheet;
          return {
            ...sheet,
            data: newData.map((row, rowIdx) =>
              row.map((cell, colIdx) => {
                const existing = sheet.data[rowIdx]?.[colIdx];
                return {
                  ...existing,
                  value: (cell as CellBase)?.value?.toString() || "",
                };
              })
            ),
          };
        })
      );
    },
    [activeTab]
  );

  const addRow = useCallback(() => {
    setSheets((prev) =>
      prev.map((sheet, idx) => {
        if (idx !== activeTab) return sheet;
        const cols = sheet.data[0]?.length || 1;
        const newRow = Array.from({ length: cols }, () => ({ value: "" }));
        return { ...sheet, data: [...sheet.data, newRow] };
      })
    );
  }, [activeTab]);

  const addColumn = useCallback(() => {
    setSheets((prev) =>
      prev.map((sheet, idx) => {
        if (idx !== activeTab) return sheet;
        return {
          ...sheet,
          data: sheet.data.map((row) => [...row, { value: "" }]),
        };
      })
    );
  }, [activeTab]);

  const removeLastRow = useCallback(() => {
    setSheets((prev) =>
      prev.map((sheet, idx) => {
        if (idx !== activeTab || sheet.data.length <= 1) return sheet;
        return { ...sheet, data: sheet.data.slice(0, -1) };
      })
    );
  }, [activeTab]);

  const removeLastColumn = useCallback(() => {
    setSheets((prev) =>
      prev.map((sheet, idx) => {
        if (idx !== activeTab || (sheet.data[0]?.length || 0) <= 1) return sheet;
        return {
          ...sheet,
          data: sheet.data.map((row) => row.slice(0, -1)),
        };
      })
    );
  }, [activeTab]);

  const addSheet = useCallback(() => {
    setSheets((prev) => {
      const newName = `Sheet${prev.length + 1}`;
      return [
        ...prev,
        {
          name: newName,
          data: Array.from({ length: 5 }, () =>
            Array.from({ length: 4 }, () => ({ value: "" }))
          ),
        },
      ];
    });
    setActiveTab(sheets.length);
  }, [sheets.length]);

  const removeSheet = useCallback(
    (tabIdx: number) => {
      if (sheets.length <= 1) return;
      setSheets((prev) => prev.filter((_, idx) => idx !== tabIdx));
      setActiveTab((prev) => (prev >= tabIdx && prev > 0 ? prev - 1 : prev));
    },
    [sheets.length]
  );

  const renameSheet = useCallback(
    (tabIdx: number, newName: string) => {
      setSheets((prev) =>
        prev.map((sheet, idx) =>
          idx === tabIdx ? { ...sheet, name: newName || sheet.name } : sheet
        )
      );
    },
    []
  );

  const downloadXlsx = useCallback(() => {
    const blob = buildXlsxBlobMultiSheet(sheets);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [sheets, fileName]);

  const rowCount = activeSheet?.data.length || 0;
  const colCount = activeSheet?.data[0]?.length || 0;

  return (
    <>
      {/* Inline card */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50/80 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <Sheet size={15} className="text-emerald-600 shrink-0" />
            <span className="text-sm font-semibold text-gray-700 truncate">
              {fileName}
            </span>
            <span className="text-xs text-gray-400">
              {sheets.length > 1 && `${sheets.length} sheets · `}
              {rowCount} rows x {colCount} cols
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={downloadXlsx}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Export Excel
            </button>
            <button
              onClick={() => setIsOpen(true)}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Full view"
            >
              <Maximize2 size={14} />
            </button>
          </div>
        </div>

        {/* Tab bar (only show if multiple sheets) */}
        {sheets.length > 1 && (
          <TabBar
            sheets={sheets}
            activeTab={activeTab}
            compact
            onTabClick={setActiveTab}
            onRenameSheet={renameSheet}
            onRemoveSheet={removeSheet}
            onAddSheet={addSheet}
          />
        )}

        {/* Inline spreadsheet */}
        <div className="overflow-auto" style={{ maxHeight: "400px" }}>
          <Spreadsheet data={activeSheet?.data || []} onChange={handleChange} />
        </div>
      </div>

      {/* Full-view modal */}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setIsOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 scrim" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative bg-white rounded-2xl text-left align-middle shadow-xl transition-all w-full lg:w-[1100px] sm:w-[90vw] h-[80vh] sm:h-[80vh] lg:h-[650px] overflow-hidden flex flex-col">
                  {/* Header */}
                  <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <Sheet
                        size={18}
                        className="text-emerald-600 shrink-0"
                      />
                      <h2 className="text-lg font-semibold text-gray-800 truncate">
                        {fileName}
                      </h2>
                      <span className="text-xs text-gray-400">
                        {sheets.length > 1 && `${sheets.length} sheets · `}
                        {rowCount} rows x {colCount} cols
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Row/Col controls */}
                      <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-0.5">
                        <button
                          onClick={addRow}
                          className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                          title="Add row"
                        >
                          <Plus size={12} />
                          Row
                        </button>
                        <button
                          onClick={addColumn}
                          className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                          title="Add column"
                        >
                          <Plus size={12} />
                          Col
                        </button>
                        <button
                          onClick={removeLastRow}
                          className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                          title="Remove last row"
                        >
                          <Trash2 size={12} />
                          Row
                        </button>
                        <button
                          onClick={removeLastColumn}
                          className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                          title="Remove last column"
                        >
                          <Trash2 size={12} />
                          Col
                        </button>
                      </div>

                      {/* Export */}
                      <button
                        onClick={downloadXlsx}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
                      >
                        Export Excel
                      </button>

                      {/* Close */}
                      <button
                        onClick={() => setIsOpen(false)}
                        className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 p-2"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Tab bar */}
                  <TabBar
                    sheets={sheets}
                    activeTab={activeTab}
                    onTabClick={setActiveTab}
                    onRenameSheet={renameSheet}
                    onRemoveSheet={removeSheet}
                    onAddSheet={addSheet}
                  />

                  {/* Content */}
                  <div className="flex-1 overflow-auto p-4">
                    <Spreadsheet
                      data={activeSheet?.data || []}
                      onChange={handleChange}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default SpreadsheetExport;
